import React from 'react';
import { Patient } from '../types/patient';
import { Activity, Moon, Heart, Flame } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

interface WearableDataProps {
  patient: Patient;
}

export default function WearableData({ patient }: WearableDataProps) {
  // Generate dummy trend data for each metric
  const stepCountData = [
    { day: 'Mon', steps: 8234 },
    { day: 'Tue', steps: 9821 },
    { day: 'Wed', steps: 7456 },
    { day: 'Thu', steps: 10234 },
    { day: 'Fri', steps: 9567 },
    { day: 'Sat', steps: 12389 },
    { day: 'Sun', steps: 11245 },
  ];

  const sleepData = [
    { day: 'Mon', hours: 6.5 },
    { day: 'Tue', hours: 7.2 },
    { day: 'Wed', hours: 6.8 },
    { day: 'Thu', hours: 7.5 },
    { day: 'Fri', hours: 6.2 },
    { day: 'Sat', hours: 8.1 },
    { day: 'Sun', hours: 7.8 },
  ];

  const heartRateData = [
    { day: 'Mon', bpm: 72 },
    { day: 'Tue', bpm: 68 },
    { day: 'Wed', bpm: 74 },
    { day: 'Thu', bpm: 70 },
    { day: 'Fri', bpm: 69 },
    { day: 'Sat', bpm: 65 },
    { day: 'Sun', bpm: 67 },
  ];

  const caloriesData = [
    { day: 'Mon', calories: 2345 },
    { day: 'Tue', calories: 2567 },
    { day: 'Wed', calories: 2123 },
    { day: 'Thu', calories: 2789 },
    { day: 'Fri', calories: 2456 },
    { day: 'Sat', calories: 3012 },
    { day: 'Sun', calories: 2834 },
  ];

  // Calculate averages
  const avgSteps = Math.round(stepCountData.reduce((sum, d) => sum + d.steps, 0) / stepCountData.length);
  const avgSleep = (sleepData.reduce((sum, d) => sum + d.hours, 0) / sleepData.length).toFixed(1);
  const avgHeartRate = Math.round(heartRateData.reduce((sum, d) => sum + d.bpm, 0) / heartRateData.length);
  const avgCalories = Math.round(caloriesData.reduce((sum, d) => sum + d.calories, 0) / caloriesData.length);

  // Reusable Chart Component
  const MetricCard = ({
    title,
    icon: Icon,
    value,
    unit,
    data,
    dataKey,
    chartType = 'line',
    color,
    iconColor
  }: {
    title: string;
    icon: any;
    value: string | number;
    unit: string;
    data: any[];
    dataKey: string;
    chartType?: 'line' | 'bar';
    color: string;
    iconColor: string;
  }) => (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <div className={`p-2 rounded-lg ${iconColor}`}>
            <Icon className="w-5 h-5" aria-hidden="true" />
          </div>
          <h3 className="font-semibold text-brand-dark-600">{title}</h3>
        </div>
      </div>

      <div className="mb-4">
        <div className="text-3xl font-bold text-brand-dark-600">{value}</div>
        <div className="text-sm text-brand-taupe-600">{unit}</div>
      </div>

      <div className="h-32">
        <ResponsiveContainer width="100%" height="100%">
          {chartType === 'line' ? (
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7ddd4" vertical={false} />
              <XAxis
                dataKey="day"
                fontSize={11}
                tick={{ fill: '#978c81' }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                fontSize={11}
                tick={{ fill: '#978c81' }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #d8cbc3',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
              <Line
                type="monotone"
                dataKey={dataKey}
                stroke={color}
                strokeWidth={2}
                dot={{ r: 3, fill: color }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          ) : (
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7ddd4" vertical={false} />
              <XAxis
                dataKey="day"
                fontSize={11}
                tick={{ fill: '#978c81' }}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                fontSize={11}
                tick={{ fill: '#978c81' }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #d8cbc3',
                  borderRadius: '8px',
                  fontSize: '12px',
                }}
              />
              <Bar dataKey={dataKey} fill={color} radius={[4, 4, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-2">
        <Activity className="w-6 h-6 text-brand-dark-500" />
        <h2 className="text-2xl font-bold text-brand-dark-600">Wearable Data</h2>
      </div>

      {/* Summary Card */}
      <div className="card bg-gradient-to-br from-brand-ivory-100 to-brand-beige-100">
        <h3 className="text-lg font-semibold text-brand-dark-600 mb-4">7-Day Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <div className="text-2xl font-bold text-brand-dark-600">{avgSteps.toLocaleString()}</div>
            <div className="text-xs text-brand-taupe-600">Avg Daily Steps</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-brand-dark-600">{avgSleep} hrs</div>
            <div className="text-xs text-brand-taupe-600">Avg Sleep</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-brand-dark-600">{avgHeartRate} bpm</div>
            <div className="text-xs text-brand-taupe-600">Avg Heart Rate</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-brand-dark-600">{avgCalories.toLocaleString()}</div>
            <div className="text-xs text-brand-taupe-600">Avg Calories Burned</div>
          </div>
        </div>
      </div>

      {/* 2x2 Grid of Charts */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Daily Step Count */}
        <MetricCard
          title="Daily Step Count"
          icon={Activity}
          value={stepCountData[stepCountData.length - 1].steps.toLocaleString()}
          unit="steps today"
          data={stepCountData}
          dataKey="steps"
          chartType="bar"
          color="#6a6055"
          iconColor="bg-brand-beige-200 text-brand-dark-600"
        />

        {/* Hours of Sleep */}
        <MetricCard
          title="Hours of Sleep"
          icon={Moon}
          value={sleepData[sleepData.length - 1].hours}
          unit="hours last night"
          data={sleepData}
          dataKey="hours"
          chartType="line"
          color="#3b82f6"
          iconColor="bg-info-100 text-info-600"
        />

        {/* Daily Average Heart Rate */}
        <MetricCard
          title="Average Heart Rate"
          icon={Heart}
          value={heartRateData[heartRateData.length - 1].bpm}
          unit="bpm today"
          data={heartRateData}
          dataKey="bpm"
          chartType="line"
          color="#ef4444"
          iconColor="bg-danger-100 text-danger-600"
        />

        {/* Calories Burned */}
        <MetricCard
          title="Calories Burned"
          icon={Flame}
          value={caloriesData[caloriesData.length - 1].calories.toLocaleString()}
          unit="calories today"
          data={caloriesData}
          dataKey="calories"
          chartType="bar"
          color="#f59e0b"
          iconColor="bg-warning-100 text-warning-600"
        />
      </div>

      {/* Additional Insights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card text-center bg-gradient-to-br from-success-50 to-success-100">
          <div className="text-2xl font-bold text-success-700">
            {stepCountData.filter(d => d.steps >= 10000).length}
          </div>
          <div className="text-sm text-success-900 font-medium mt-1">Days with 10K+ Steps</div>
        </div>

        <div className="card text-center bg-gradient-to-br from-info-50 to-info-100">
          <div className="text-2xl font-bold text-info-700">
            {sleepData.filter(d => d.hours >= 7).length}
          </div>
          <div className="text-sm text-info-900 font-medium mt-1">Days with 7+ Hours Sleep</div>
        </div>

        <div className="card text-center bg-gradient-to-br from-warning-50 to-warning-100">
          <div className="text-2xl font-bold text-warning-700">
            {caloriesData.filter(d => d.calories >= 2500).length}
          </div>
          <div className="text-sm text-warning-900 font-medium mt-1">Days with 2500+ Cal Burned</div>
        </div>
      </div>
    </div>
  );
}
